// frontend/modules/in-app-notifications/hooks/useInAppNotifications.ts
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useInAppNotificationStore } from '../stores/in-app-notification-store';
import {
    CreateNotificationRequest, 
    NotificationFilters 
} from '../types';
import {
    useGetNotifications,
    useGetNotificationStats,
    useGetNotificationById,
    useMarkAsRead,
    useMarkAsAttended,
    useMarkAllAsRead,
    useDeleteNotification,
    useCreateNotification,
    getGetNotificationsQueryKey,
    getGetNotificationStatsQueryKey
} from '@/shared/api/client/in-app-notifications';

export const useInAppNotifications = () => {
    const queryClient = useQueryClient();
    const {
        notifications,
        selectedNotification,
        stats,
        filters,
        isLoading,
        error,
        currentPage,
        pageSize,
        totalElements,
        totalPages,
        setNotifications,
        setSelectedNotification,
        setStats,
        setFilters,
        setLoading,
        setError,
        setCurrentPage,
        setPageSize,
        setPaginationInfo,
        markAsRead: markAsReadInStore,
        markAsAttended: markAsAttendedInStore,
        removeNotification,
        updateNotificationInList,
        reset
    } = useInAppNotificationStore();

    // Query for notifications
    const notificationsQuery = useGetNotifications({
        page: currentPage,
        size: pageSize,
        unreadOnly: filters.unreadOnly
    }, {
        query: {
            staleTime: 30000, // 30 seconds
            refetchInterval: 60000, // Refetch every minute
        }
    });

    // Query for notification stats
    const statsQuery = useGetNotificationStats({
        query: {
            staleTime: 30000,
            refetchInterval: 60000,
        }
    });

    // Query for single notification
    const getNotificationById = (id: number) => {
        return useGetNotificationById(id, {
            query: {
                enabled: !!id,
            }
        });
    };

    // Mutations
    const markAsReadMutation = useMarkAsRead({
        mutation: {
            onSuccess: (_ :any, variables) => {
                markAsReadInStore(variables.id);
                queryClient.invalidateQueries({ queryKey: getGetNotificationStatsQueryKey() });
                message.success('Notification marked as read');
            },
            onError: (error) => {
                console.error('Error marking notification as read:', error);
                message.error('Failed to mark notification as read');
            }
        }
    });

    const markAsAttendedMutation = useMarkAsAttended({
        mutation: {
            onSuccess: (_, variables) => {
                markAsAttendedInStore(variables.id);
                queryClient.invalidateQueries({ queryKey: getGetNotificationStatsQueryKey() });
                message.success('Notification marked as attended');
            },
            onError: (error) => {
                console.error('Error marking notification as attended:', error);
                message.error('Failed to mark notification as attended');
            }
        }
    });

    const markAllAsReadMutation = useMarkAllAsRead({
        mutation: {
            onSuccess: (response) => {
                const updatedCount = response;
                queryClient.invalidateQueries({ queryKey: getGetNotificationsQueryKey() });
                queryClient.invalidateQueries({ queryKey: getGetNotificationStatsQueryKey() });
                message.success(`${updatedCount} notifications marked as read`);
            },
            onError: (error) => {
                console.error('Error marking all notifications as read:', error);
                message.error('Failed to mark all notifications as read');
            }
        }
    });

    const deleteNotificationMutation = useDeleteNotification({
        mutation: {
            onSuccess: (_, variables) => {
                removeNotification(variables.id);
                queryClient.invalidateQueries({ queryKey: getGetNotificationStatsQueryKey() });
                message.success('Notification deleted');
            },
            onError: (error) => {
                console.error('Error deleting notification:', error);
                message.error('Failed to delete notification');
            }
        }
    });

    const createNotificationMutation = useCreateNotification({
        mutation: {
            onSuccess: (newNotification) => {
                queryClient.invalidateQueries({ queryKey: getGetNotificationsQueryKey() });
                queryClient.invalidateQueries({ queryKey: getGetNotificationStatsQueryKey() });
                message.success('Notification created successfully');
            },
            onError: (error) => {
                console.error('Error creating notification:', error);
                message.error('Failed to create notification');
            }
        }
    });

    // Update store when query data changes
    React.useEffect(() => {
        if (notificationsQuery.data) {
            const pageData = notificationsQuery.data;
            setNotifications(pageData.content || []);
            setPaginationInfo(pageData.totalElements || 0, pageData.totalPages || 0);
        }
    }, [notificationsQuery.data, setNotifications, setPaginationInfo]);

    React.useEffect(() => {
        const statsData = statsQuery.data;
        if (statsData) {
            setStats({
                totalCount: statsData.totalCount || 0,
                unreadCount: statsData.unreadCount || 0,
                attendedCount: statsData.attendedCount || 0,
                expiredCount: statsData.expiredCount || 0
            });
        }
    }, [statsQuery.data, setStats]);

    React.useEffect(() => {
        setLoading(notificationsQuery.isLoading || statsQuery.isLoading);
    }, [notificationsQuery.isLoading, statsQuery.isLoading, setLoading]);

    React.useEffect(() => {
        const error = notificationsQuery.error || statsQuery.error;
        setError(error ? String(error) : null);
    }, [notificationsQuery.error, statsQuery.error, setError]);

    // Helper functions
    const refreshNotifications = () => {
        queryClient.invalidateQueries({ queryKey: getGetNotificationsQueryKey() });
    };

    const refreshStats = () => {
        queryClient.invalidateQueries({ queryKey: getGetNotificationStatsQueryKey() });
    };

    const updateFilters = (newFilters: NotificationFilters) => {
        setFilters(newFilters);
        setCurrentPage(0); // Reset to first page when filters change
    };

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const changePageSize = (size: number) => {
        setPageSize(size);
        setCurrentPage(0); // Reset to first page when page size changes
    };

    return {
        // State
        notifications,
        selectedNotification,
        stats,
        filters,
        isLoading,
        error,
        currentPage,
        pageSize,
        totalElements,
        totalPages,

        // Query functions
        getNotificationById,
        refreshNotifications,
        refreshStats,

        // Actions
        markAsRead: (id: number) => markAsReadMutation.mutateAsync({ id }),
        markAsAttended: (id: number) => markAsAttendedMutation.mutateAsync({ id }),
        markAllAsRead: () => markAllAsReadMutation.mutateAsync(),
        deleteNotification: (id: number) => deleteNotificationMutation.mutateAsync({ id }),
        createNotification: (request: CreateNotificationRequest) => 
            createNotificationMutation.mutateAsync({ data: request }),

        // Mutation states
        isMarkingAsRead: markAsReadMutation.isPending,
        isMarkingAsAttended: markAsAttendedMutation.isPending,
        isMarkingAllAsRead: markAllAsReadMutation.isPending,
        isDeleting: deleteNotificationMutation.isPending,
        isCreating: createNotificationMutation.isPending,

        // Helpers
        updateFilters,
        goToPage,
        changePageSize,
        setSelectedNotification,
        reset,
    };
};
